import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { FormLabel } from '@components/form-label';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconCode } from '@components/icons/icon-code';
import { IconSend } from '@components/icons/send';
import { LabelTextGroup } from '@components/label-text-group';
import { NoResultCard } from '@components/no-result-card';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { fieldTypeIconMap } from '@components/text-editors/addons/field-mapping-type-maps';
import { useScopedTranslate } from '@locale/translate';
import { camelize } from '@utils/strings';

import { useUpdateJourneyTravellerAction } from 'api/traveller-actions/hooks';
import { MappableActionVariable, TravellerJourneyActionDetailed } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { getActionStatus } from 'ui/authenticated/main/helpers';

import { ActionType, CodeData, CodeFormFields, formatFormData } from './common';

import {
  ActionProgressBar,
  FormSidebarFooter,
  MappedSource,
  SidebarProps,
  TravellerActionFooter,
  TravellerActionSidebar,
  isActionError,
  useEditingContext,
} from '../common';

const ACTION_TYPE = ACTION_TYPES.code;

export const SidebarContent = ({ shortId }: SidebarProps) => (
  <TravellerActionSidebar
    shortId={shortId}
    actionType={ACTION_TYPE}
    Details={DetailsContent}
    Edit={FormContent}
  />
);

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const status = getActionStatus(action);

  const progressText =
    status === 'completed'
      ? t('codeExecuted')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  const outputVariables = Object.entries(action.variables ?? {});

  return (
    <Stack space={4}>
      <ActionProgressBar
        action={action}
        isError={isError}
        isCard={false}
        progressText={progressText}
      />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <Text text={t('selectedField')} font="semibold14" />
          {action.values?.inputs?.map((input) => {
            const referenceVariables = input?.variables ?? {};
            const firstReferenceVariable = Object.values(referenceVariables)[0];
            const referenceVariablesKeys = Object.keys(referenceVariables);
            const isAttributeReference =
              referenceVariablesKeys.length > 0 &&
              (firstReferenceVariable.source.startsWith('company_traveller_attribute') ||
                firstReferenceVariable.source.startsWith('journey_attribute'));
            const isTravellerReference =
              firstReferenceVariable?.source === 'company_traveller' ||
              firstReferenceVariable?.source === 'traveller';
            const rawReferenceActionId =
              !isAttributeReference && referenceVariablesKeys.length > 0
                ? (referenceVariables[referenceVariablesKeys[0]] as MappableActionVariable).action
                : '';
            const referenceActionId = rawReferenceActionId.replace(/_MISSING$/, '');
            const fieldLabel = firstReferenceVariable?.label ?? '';
            return (
              <MappedSource
                label={fieldLabel}
                isAttribute={isAttributeReference || isTravellerReference}
                isActionMapped={Boolean(referenceActionId)}
                isUploaded={!isAttributeReference && !referenceActionId && !!action.data.document}
                referenceActionId={referenceActionId}
              />
            );
          })}
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('codeOutput')}>
        <Stack space={4}>
          {action.status === 'completed' ? (
            Object.entries(action.variables).map(([fieldName, options]) => {
              const value = action.data[camelize(fieldName)];
              return (
                <div key={fieldName}>
                  <FormLabel text={options.label} />
                  <Text text={value ? value.toString() : ''} />
                </div>
              );
            })
          ) : (
            <NoResultCard icon={IconCode} text={t('actionNotRun')} />
          )}
          <Card variant="default" space={4}>
            <Stack space={1}>
              {outputVariables.map(([key, value]) => {
                const Icon = fieldTypeIconMap[value.type];
                return (
                  <Stack key={key} orientation="horizontal" space={2}>
                    <Icon color="elementPurpleInvert" />
                    <Text text={value.label.replaceAll("'", '').replaceAll('"', '')} />
                  </Stack>
                );
              })}
            </Stack>
          </Card>
        </Stack>
      </HideContainer>
      <TravellerActionFooter
        action={action}
        startIcon={<IconSend />}
        startText={t('create')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

const Form = createForm<CodeData>();

const FormContent = ({ action }: ContentProps) => {
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const { stopEditing } = useEditingContext();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = (data: CodeData) => {
    return editMutation.mutateAsync(formatFormData(data));
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <CodeFormFields Form={Form} data={action} />
      <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    dataNotFound: 'Data not found',
    description: 'Description',
    codeExecuted: 'Code executed',
    codeOutput: 'Output',
    error: 'Error',
    inProgress: 'In progress',
    notStarted: 'Not started',
    actionNotRun: 'Action has not been run',
    inputs: 'Inputs',
    create: 'Create',
    retry: 'Re-create',
    actionDetails: 'Action details',
    selectedField: 'Selected fields',
  },
};
